import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { CalendarLink, MyProfile, MySession, NotificationPrefs, PersonCard, ProfilePatch } from './types';
import { isDemo } from './index';
import { httpProfileApi } from './http';
import { mockProfileApi } from './mock';

/** «Мой профиль» and other people's cards: one set of endpoints for both sides of the portal. */
export interface ProfileApi {
  /** `side` only tells the demo whom «I» am; the BFF knows it from the session. */
  profile(side: 'client' | 'studio'): Promise<MyProfile>;
  saveProfile(side: 'client' | 'studio', patch: ProfilePatch): Promise<MyProfile>;
  sessions(): Promise<MySession[]>;
  /** Ends every other session of the caller; the number ended. */
  revokeOthers(): Promise<{ ended: number }>;
  prefs(): Promise<NotificationPrefs>;
  savePrefs(p: NotificationPrefs): Promise<NotificationPrefs>;
  /** The caller's secret calendar feed (made on first ask). */
  calendar(): Promise<CalendarLink>;
  /** A new secret: the old link stops working. */
  resetCalendar(): Promise<CalendarLink>;
  /** 404 when the viewer may not open this person. */
  person(id: string): Promise<PersonCard>;
}

export const profileApi: ProfileApi = isDemo ? mockProfileApi : httpProfileApi;

type Side = 'client' | 'studio';

export const useMyProfile = (side: Side) => useQuery({ queryKey: ['me', 'profile', side], queryFn: () => profileApi.profile(side) });
export const useMySessions = () => useQuery({ queryKey: ['me', 'sessions'], queryFn: profileApi.sessions });
export const useMyPrefs = () => useQuery({ queryKey: ['me', 'prefs'], queryFn: profileApi.prefs });
/** One person's card, fetched when the dialog opens. A 404 is an answer, not a failure to retry. */
export const usePersonCard = (id: string, enabled = true) => useQuery({ queryKey: ['person', id], queryFn: () => profileApi.person(id), enabled, retry: false });

export function useSaveProfile(side: Side) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: ProfilePatch) => profileApi.saveProfile(side, patch),
    // The name shows in the top bar, the team list and threads: refresh all of it.
    onSuccess: () => qc.invalidateQueries(),
    meta: { inline: true },
  });
}
export function useRevokeOtherSessions() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: profileApi.revokeOthers, onSuccess: () => qc.invalidateQueries({ queryKey: ['me', 'sessions'] }), meta: { inline: true } });
}
export function useSaveMyPrefs() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: profileApi.savePrefs, onSuccess: (p) => qc.setQueryData(['me', 'prefs'], p) });
}
